import React, {useMemo, useRef} from 'react'
import _ from 'lodash'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import {
    Box,
    Skeleton,
    useBreakpointValue,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import PropTypes from 'prop-types'
import {useChordEvents} from '../../../hooks/use-chord-events'

import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {styleConfigChakraKeys} from '../../../constants'
import Link from '../../link'
import AccountDropdown from './account-dropdown'

const LoginAction = ({
    accountBtn,
    isRegistered = false,
    setShowLoading,
    showLoading = true,
    dropdownData
}) => {
    const navigate = useNavigation()
    const hasEnterPopoverContent = useRef()
    const {data: customer, isLoading: isLoadingCustomer} = useCurrentCustomer()
    const {isOpen, onClose, onOpen} = useDisclosure()
    const {trackSignOut} = useChordEvents()

    const styles = useMultiStyleConfig(styleConfigChakraKeys.header)

    const logout = useAuthHelper(AuthHelpers.Logout)
    const isDesktop = useBreakpointValue({
        base: false,
        xl: true
    })

    const onSignoutClick = async () => {
        setShowLoading(true)
        await logout.mutateAsync()
        trackSignOut({
            email: customer?.email
        })
        navigate('/login')
        setShowLoading(false)
    }

    const keyMap = {
        Escape: () => onClose(),
        Enter: () => onOpen()
    }

    const handleIconsMouseLeave = () =>
        setTimeout(() => {
            if (!hasEnterPopoverContent.current) onClose()
        }, 100)

    const accountBtnSettings = useMemo(() => {
        const defaultText = accountBtn?.text
        const defaultIcon = accountBtn?.iconName
        const btnLoginAction = !defaultText ? {icon: defaultIcon} : {}
        const defaultBtnSettings = {
            type: accountBtn?.type ?? 'tertiary'
        }

        if (!isRegistered)
            return {
                ..._.omit(accountBtn, ['iconName']),
                ...btnLoginAction,
                'aria-label': defaultText,
                url: '/login',
                buttonStyles: {
                    ...styles.iconBtn,
                    width: 'auto',
                    minWidth: '50px',
                    height: 'auto',
                    fontWeight: 300,
                    textTransform: 'uppercase',
                    textAlign: 'center'
                },
                _hover: {
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: '5px'
                }
            }

        return {
            ...defaultBtnSettings,
            icon: defaultIcon,
            iconStyles: styles.accountIcon,
            padding: '0',
            margin: '0',
            _hover: {
                transform: 'scale(1.05)'
            }
        }
    }, [isRegistered, customer, accountBtn])

    if (showLoading || (isRegistered && isLoadingCustomer))
        return (
            <Skeleton
                width="33px"
                height="33px"
                marginRight="20px"
                display={{base: 'none', xl: 'block'}}
            />
        )

    if (!isRegistered)
        return (
            <Link
                buttonStyles={{
                    width: 'auto',
                    height: 'auto'
                }}
                {...accountBtnSettings}
                tabIndex={0}
                display="contents"
            />
        )

    return (
        <Box onMouseLeave={handleIconsMouseLeave} onMouseEnter={onOpen}>
            {accountBtnSettings && (
                <Link
                    buttonStyles={{
                        width: 'auto',
                        height: 'auto'
                    }}
                    {...accountBtnSettings}
                    tabIndex={0}
                    onMouseOver={isDesktop && isRegistered && onOpen}
                    display={{base: 'none', xl: 'contents'}}
                />
            )}

            {isOpen && isRegistered && isHydrated() && (
                <AccountDropdown
                    isOpen={!!isOpen}
                    onClose={onClose}
                    onOpen={onOpen}
                    handleIconsMouseLeave={handleIconsMouseLeave}
                    hasEnterPopoverContent={hasEnterPopoverContent}
                    keyMap={keyMap}
                    onSignoutClick={onSignoutClick}
                    styles={styles}
                    dropdownData={dropdownData}
                />
            )}
        </Box>
    )
}

LoginAction.propTypes = {
    accountBtn: PropTypes.object,
    dropdownData: PropTypes.array,
    isRegistered: PropTypes.bool,
    showLoading: PropTypes.bool,
    setShowLoading: PropTypes.func
}

export default LoginAction
